import { useRef, useState } from 'react';
import { Autocomplete, Grid, Group, Loader, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useEmployerSearch } from '@/hooks/useEmployerSearch';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';

interface EmployerRowProps {
  value: string | undefined;
  /**
   * Absent for a role the employer directory refuses (it admits payments roles only): they read the
   * row with no edit affordance. Rejects if the write fails; the row stays open.
   */
  onChange?: (employer: string) => Promise<void>;
}

const TEXT = { fontSize: 14, lineHeight: '20px' } as const;

/**
 * The plan sponsor, on every track: a self-pay patient's employer is how a later employer case is
 * found. Click the value to search the employer directory, as the PN card's employer box does;
 * Enter keeps free text the directory doesn't know.
 */
export function EmployerRow({ value, onChange }: EmployerRowProps) {
  const [query, setQuery] = useState<string>();
  const editing = query !== undefined;
  const { options, searching } = useEmployerSearch(query ?? '', { skip: !editing, current: value });
  // Enter on a highlighted option fires both the option submit and the key handler, in an order
  // Mantine doesn't promise: the key handler waits a microtask and stands down if an option was
  // picked, so the option wins and the half-typed text is never written.
  const saving = useRef(false);
  const optionPicked = useRef(false);

  const save = async (employer: string): Promise<void> => {
    const trimmed = employer.trim();
    if (saving.current) return;
    if (!onChange || !trimmed || trimmed === value) {
      setQuery(undefined);
      return;
    }
    saving.current = true;
    try {
      await onChange(trimmed);
      setQuery(undefined);
    } catch {
      // The caller has said so; the box stays open with what was typed.
    } finally {
      saving.current = false;
    }
  };

  return (
    <Grid align="flex-start" style={{ width: '100%' }}>
      <Grid.Col span={3}>
        <Text style={{ fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY }}>Employer</Text>
      </Grid.Col>
      <Grid.Col span={9}>
        {editing ? (
          <Autocomplete
            aria-label="Employer"
            value={query}
            onChange={setQuery}
            data={options}
            onOptionSubmit={(employer) => {
              optionPicked.current = true;
              void save(employer);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const typed = query ?? '';
                queueMicrotask(() => {
                  if (optionPicked.current) {
                    optionPicked.current = false;
                    return;
                  }
                  void save(typed);
                });
              }
              if (event.key === 'Escape') setQuery(undefined);
            }}
            onBlur={() => !saving.current && setQuery(undefined)}
            rightSection={searching ? <Loader size={12} /> : null}
            size="xs"
            radius="md"
            autoFocus
            comboboxProps={{ withinPortal: true }}
            styles={{ input: { height: 20, minHeight: 20, fontSize: 13 } }}
            style={{ width: '100%' }}
          />
        ) : onChange ? (
          <Group
            gap={5}
            wrap="nowrap"
            onClick={() => setQuery(value ?? '')}
            style={{ cursor: 'pointer', display: 'inline-flex' }}
          >
            <Text style={{ ...TEXT, color: value ? 'var(--mantine-color-black)' : ORBIT_DISABLED }}>
              {value ?? 'Not set'}
            </Text>
            <IconChevronDown size={11} stroke={1.6} style={{ color: ORBIT_GRAY, flexShrink: 0 }} />
          </Group>
        ) : (
          <Text style={{ ...TEXT, color: value ? 'var(--mantine-color-black)' : ORBIT_DISABLED }}>
            {value ?? 'Not set'}
          </Text>
        )}
      </Grid.Col>
    </Grid>
  );
}
